import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Package, Truck, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '../supabaseClient';

const OrderTracking = () => {
  const [searchParams] = useSearchParams();
  const [orderId, setOrderId] = useState(searchParams.get('id') || '');
  const [orderStatus, setOrderStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  // ---------------- FETCH ORDER FROM DB ----------------
  const fetchOrder = async (id) => {
    if (!id) return;

    setLoading(true);

    try {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          order_items (
            quantity,
            size,
            price,
            products (
              name,
              images
            )
          )
        `)
        .eq('order_id', id)
        .single();

      if (error || !data) {
        setOrderStatus(null);
        setLoading(false);
        return;
      }

      const stepsTemplate = [
        { label: 'Order Placed', key: 'pending' },
        { label: 'Processing', key: 'processing' },
        { label: 'Shipped', key: 'shipped' },
        { label: 'Delivered', key: 'delivered' }
      ];

      const steps = stepsTemplate.map((step) => ({
        label: step.label,
        completed:
          step.key === 'pending' ||
          step.key === data.status ||
          (step.key === 'processing' &&
            ['processing', 'shipped', 'delivered'].includes(data.status)) ||
          (step.key === 'shipped' &&
            ['shipped', 'delivered'].includes(data.status)) ||
          (step.key === 'delivered' && data.status === 'delivered'),
        date:
          step.key === 'pending'
            ? new Date(data.created_at).toLocaleDateString()
            : null
      }));

      setOrderStatus({
          id: data.order_id,
        date: new Date(data.created_at).toLocaleDateString(),
        status: data.status || 'pending',
        steps,
        items: data.order_items || []
      });

      setLoading(false);
    } catch (err) {
      console.error(err);
      setOrderStatus(null);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchParams.get('id')) {
      fetchOrder(searchParams.get('id'));
    }
  }, [searchParams]);

  const handleTrack = (e) => {
    e.preventDefault();
    fetchOrder(orderId);
  };

  return (
    <div className="bg-cream min-h-screen">
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12">

        {/* TITLE */}
        <h1 className="font-serif text-4xl md:text-5xl text-charcoal mb-12 text-center">
          Track Your Order
        </h1>

        {/* INPUT */}
        <div className="bg-white rounded-xl shadow-md border border-[#E5E0D8] p-8 mb-12">
          <form onSubmit={handleTrack} className="flex gap-4">
            <input
              type="text"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              placeholder="Enter Order ID"
              className="flex-1 bg-transparent border-b border-[#E5E0D8] px-0 py-3 focus:border-bronze focus:outline-none"
            />
            <button
              type="submit"
              className="bg-bronze text-white px-8 py-3 rounded-full hover:bg-bronze-dark transition-all duration-300 text-sm uppercase tracking-widest font-medium"
            >
              Track
            </button>
          </form>
        </div>

        {/* SUCCESS */}
        {searchParams.get('success') === 'true' && (
          <div className="bg-green-50 border border-green-200 p-6 rounded-lg mb-12 flex gap-4">
            <CheckCircle className="text-green-600" size={24} />
            <div>
              <h2 className="font-serif text-xl text-green-900 mb-1">
                Order Placed Successfully!
              </h2>
              <p className="text-sm text-green-700">
                Thank you for your order.
              </p>
            </div>
          </div>
        )}

        {/* LOADING */}
        {loading && <p className="text-center">Loading...</p>}

        {/* ORDER DETAILS */}
        {orderStatus && (
          <>
            {/* HEADER */}
            <div className="bg-white rounded-xl shadow-md border border-[#E5E0D8] p-8 mb-8 flex justify-between items-center">
              <div>
                <h2 className="font-serif text-3xl text-charcoal">
                  Order #{orderStatus.id}
                </h2>
                <p className="text-sm text-[#6B665F] mt-1">
                  Placed on {orderStatus.date}
                </p>
              </div>

              <span
                className={`px-5 py-2 rounded-full text-xs font-semibold uppercase tracking-wider
                  ${
                    orderStatus.status === 'pending'
                      ? 'bg-amber-100 text-amber-700'
                      : orderStatus.status === 'processing'
                      ? 'bg-blue-100 text-blue-700'
                      : orderStatus.status === 'shipped'
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-green-100 text-green-700'
                  }`}
              >
                {orderStatus.status}
              </span>
            </div>

            {/* TIMELINE */}
            <div className="bg-white rounded-xl shadow-md border border-[#E5E0D8] p-8 mb-8">
              <div className="flex justify-between">
                {orderStatus.steps.map((step, index) => (
                  <div key={index} className="flex-1 text-center">
                    <div
                      className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center
                        ${
                          step.completed
                            ? 'bg-[#ba8245] text-white'
                            : 'bg-gray-200 text-gray-400'
                        }`}
                    >
                      {index === 0 && <Package size={20} />}
                      {index === 1 && <AlertCircle size={20} />}
                      {index === 2 && <Truck size={20} />}
                      {index === 3 && <CheckCircle size={20} />}
                    </div>
                    <p className="mt-3 text-sm font-medium">{step.label}</p>
                    {step.date && (
                      <p className="text-xs text-gray-500">{step.date}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* ITEMS */}
            <div className="bg-white rounded-xl shadow-md border border-[#E5E0D8] p-8">
              <h3 className="font-serif text-2xl mb-6">Items</h3>

              <div className="space-y-6">
                {orderStatus.items.map((item, i) => (
                  <div
                    key={i}
                    className="flex gap-6 items-center border border-[#F1EDE7] p-4 rounded-lg"
                  >
                    <img
                      src={item.products.images[0]}
                      alt=""
                      className="w-24 h-28 object-cover rounded-md"
                    />

                    <div className="flex-1">
                      <p className="font-medium text-charcoal">
                        {item.products.name}
                      </p>
                      <p className="text-sm text-[#6B665F]">
                        Size: {item.size} • Qty: {item.quantity}
                      </p>
                    </div>

                    <p className="font-semibold text-[#ba8245]">
                      ₹{item.price}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {!loading && orderId && !orderStatus && (
          <div className="text-center text-gray-500">
            No order found with ID {orderId}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderTracking;